import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { PrimeReactProvider } from "primereact/api";
import { primeReactConfig } from "./utils/serviceData.js";

// Import styles
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css"; // Add this import
import "primeflex/primeflex.css";
import "primeicons/primeicons.css";
import "./App.scss";
import "./Icon/icomoon/style.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./assets/styles/common.scss";

// Import components
import Layout from "./components/layout/Layout.jsx";

// Import route configuration
import { generateRoutes, authRoutes, publicRoutes } from "./config/routes.js";

// Import utilities and common components
import ProtectedRoute from "./route/protectedRoute.js";
import ToastComponent from "./components/common/toast.js";
import { Navigate } from "react-router-dom";
import { commonGetApi } from "./utils/apiService.js";
import { API_ENDPOINTS } from "./utils/apiEndpoints.js";
import { userProfile } from "./redux/commonSlice.js";
import Loader from "./components/common/loader.js";

function App() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  
  const assignedPermission = useSelector(
    (state) => state?.common?.user?.app_permission_ids || []
  );
  const userProfileData = useSelector((state) => state?.common?.user);
  const isAuthenticated = !!userProfileData?.user_id;
  const routes = generateRoutes(assignedPermission);

  useEffect(() => {
    const checkSession = async () => {
      if (userProfileData?.user_id) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await commonGetApi(API_ENDPOINTS.USER_PROFILE);
        if (response?.data?.data?.user_id) {
          dispatch(userProfile(response.data.data));
          
          if (response.data.data.initial_organization_id) {
            localStorage.setItem("organization_id", response.data.data.initial_organization_id);
          }
          if (response.data.data.initial_camp_details_id) {
            localStorage.setItem("camp_id", response.data.data.initial_camp_details_id);
          }
        }
      } catch (error) {
        console.log("No active session", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Loader />
      </div>
    );
  }

  return (
    <PrimeReactProvider value={primeReactConfig}>
      <div className="App">
        <Router>
          <Routes>
            <Route 
              path="/" 
              element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} 
            />
            
            {authRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={isAuthenticated ? <Navigate to="/dashboard" replace /> : route.element}
              />
            ))}
            
            {publicRoutes.map((route, index) => (
              <Route
                key={`public-${index}`}
                path={route.path}
                element={route.element}
              />
            ))}
            
            <Route element={isAuthenticated ? <Layout /> : <Navigate to="/login" replace />}>
              {routes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  element={
                    <ProtectedRoute
                      feature={route.feature}
                      element={route.element}
                    />
                  }
                />
              ))}
            </Route>
          </Routes>
        </Router>
        <ToastComponent />
      </div>
    </PrimeReactProvider>
  );
}

export default App;
